import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import cloudimalogo from '../assets/images/cloudima_png-01.png';

// API base URL (defaults to prod)
const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    'https://s625qqv16k.execute-api.eu-west-1.amazonaws.com/prod';

const DemoForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [cookiesAccepted, setCookiesAccepted] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        profession: '',
        email: '',
        company: '',
        request_type: 'demo',
        preferred_date: '',
        preferred_time: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showDateTime, setShowDateTime] = useState(true);
    const [isExistingRequest, setIsExistingRequest] = useState(false);

    useEffect(() => {
        const cookiesAccepted = localStorage.getItem('cookiesAccepted') === 'true';
        setCookiesAccepted(cookiesAccepted);

        // Obtener email solo si viene en la URL o en el estado
        const emailFromUrl = new URLSearchParams(location.search).get('email');
        const emailFromState = location.state?.email;
        
        if (emailFromUrl || emailFromState) {
            const email = emailFromUrl || emailFromState;
            setFormData(prev => ({ ...prev, email }));
            
            // Enviar email de interés inicial inmediatamente
            sendInitialInterestEmail(email);
            
            // Verificar si existe una solicitud previa
            checkExistingRequest(email);
        }
    }, [location]);

    const sendInitialInterestEmail = async (email) => {
        try {
            
            const response = await fetch(`${API_BASE_URL}/api/send-email`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    to: 'ismael@cloudima.ai, pedro.garcia@cloudima.ai',
                    subject: `🎯 Nuevo Interés Inicial - ${email}`,
                    template: 'initial-interest',
                    data: {
                        email: email,
                        timestamp: new Date().toLocaleString('es-ES')
                    }
                })
            });

        } catch (error) {
            console.error('Error enviando email de interés inicial:', error);
        }
    };

    const checkExistingRequest = async (email) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/requests/${encodeURIComponent(email)}`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 404) {
                    // No existe una solicitud previa, esto es normal
                    return;
                }
                throw new Error('Error checking existing request');
            }
            const data = await response.json();
            
            if (data) {
                // Actualizar el formulario con los datos existentes
                setFormData(prev => ({
                    ...prev,
                    name: data.contact_name || '',
                    profession: data.profession || '',
                    company: data.company_name || '',
                    request_type: data.request_type || 'demo',
                    preferred_date: data.preferred_date || '',
                    preferred_time: data.preferred_time || '',
                    message: data.message || ''
                }));
                setIsExistingRequest(true);
                setShowDateTime(data.request_type === 'demo' || data.request_type === 'both');
            }
        } catch (error) {
            console.error('Error checking existing request:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'request_type') {
            setShowDateTime(value === 'demo' || value === 'both');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const url = isExistingRequest
                ? `${API_BASE_URL}/api/requests/${encodeURIComponent(formData.email)}`
                : `${API_BASE_URL}/api/requests`;

            const response = await fetch(url, {
                method: isExistingRequest ? 'PUT' : 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    contact_name: formData.name,
                    email: formData.email,
                    company_name: formData.company,
                    profession: formData.profession,
                    request_type: formData.request_type,
                    preferred_date: formData.preferred_date,
                    preferred_time: formData.preferred_time,
                    timezone: formData.timezone,
                    message: formData.message
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error submitting form');
            }

            // Enviar correo de confirmación
            const emailResponse = await fetch(`${API_BASE_URL}/api/send-email`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    to: formData.email,
                    subject: isExistingRequest ? 
                        `Your request has been updated` :
                        `Thank you for your demo request`,
                    template: isExistingRequest ? 'update-confirmation' : 'demo-confirmation',
                    data: {
                        name: formData.name,
                        type: formData.request_type,
                        date: formData.preferred_date,
                        time: formData.preferred_time,
                        timezone: formData.timezone
                    }
                })
            });

            if (!emailResponse.ok) {
                console.error('Error sending confirmation email');
            }

            navigate('/thank-you');
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptCookies = () => {
        localStorage.setItem('cookiesAccepted', 'true');
        setCookiesAccepted(true);
    };

    // Generate time slots from 9 AM to 6 PM
    const timeSlots = Array.from({ length: 19 }, (_, i) => {
        const hour = Math.floor((i + 18) / 2);
        const minute = (i + 18) % 2 === 0 ? '00' : '30';
        const time = `${hour.toString().padStart(2, '0')}:${minute}`;
        return time;
    });

    return (
        <div className="form-page bg-gradient-to-r min-h-screen flex items-center justify-center p-4">
            <div className="bg-white px-8 py-4 rounded-xl shadow-lg max-w-md w-full">
                <div className="flex justify-between items-center p-1">
                    <img
                        src={cloudimalogo}
                        alt="Cloudima Logo"
                        className="opacity-85 ml-auto w-40 md:w-30 lg:w-40 rounded-full mr-auto hover:transition duration-300"
                    />
                </div>
                <h1 className="text-2xl font-bold mb-6 text-gray-800 text-center">
                    {isExistingRequest ? 'Update Your Request' : 'Join Our Platform'}
                </h1>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {isExistingRequest && (
                    <div className="mb-4 p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded">
                        We found your previous request. You can update your information below.
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="name" className="block text-gray-700 mb-2">
                            Full Name:
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="profession" className="block text-gray-700 mb-2">
                            Job Title:
                        </label>
                        <input
                            type="text"
                            id="profession"
                            name="profession"
                            required
                            value={formData.profession}
                            onChange={handleChange}
                            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="email" className="block text-gray-700 mb-2">
                            Email:
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="company" className="block text-gray-700 mb-2">
                            Company:
                        </label>
                        <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="message" className="block text-gray-700 mb-2">
                            Additional Message:
                        </label>
                        <textarea
                            id="message"
                            name="message"
                            rows="4"
                            value={formData.message}
                            onChange={handleChange}
                            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-indigo-500 text-white p-3 rounded-lg hover:bg-indigo-600 transition-colors duration-300"
                    >
                        {loading ? 'Submitting...' : isExistingRequest ? 'Update Request' : 'Submit Request'}
                    </button>
                </form>
            </div>

            {!cookiesAccepted && (
                <div id="cookie-banner" className="fixed bottom-0 w-full bg-gray-800 text-white p-4 z-50">
                    <div className="container mx-auto flex justify-between items-center">
                        <p className="text-sm">
                            We use cookies to improve your experience on our site. By continuing to use our site, you accept our use of cookies.
                        </p>
                        <button
                            onClick={handleAcceptCookies}
                            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition duration-300"
                        >
                            Accept
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DemoForm; 
